import * as React from 'react';
import { cn } from '../../../utils';
import { ZINE_ROOT_CLASS } from './zine-theme';

/**
 * The full zine stylesheet, transposed 1:1 from the design spec.
 * Every class is prefixed with `z-` and every custom property with `--z-`
 * to avoid collisions; all rules are scoped under the `.z-issue` root.
 * Deliberately single-theme: a printed zine — cream paper, poster red,
 * protest-press black — regardless of the app theme.
 */
export const zineCss = `
.z-issue {
  --z-paper: #FFF8F5;
  --z-cream: #FEF3EF;
  --z-ink: #1A1018;
  --z-mid: #6B4F55;
  --z-warm-gray: #756560;
  --z-red: #D41920;
  --z-red-applied: #CF2E2E;
  --z-sizzle: #E84B5A;
  --z-charm: #F2A0C4;
  --z-baby: #F8D7DD;
  --z-gold: #FFD700;
  --z-line: #F0DAD5;
  --z-good: #2E7D5B;
  --z-good-soft: #E7F4EC;

  --z-font-cond: 'Oswald', 'Avenir Next Condensed', 'Arial Narrow', 'Helvetica Neue', sans-serif;
  --z-font-serif: 'DM Serif Text', Georgia, 'Times New Roman', serif;
  --z-font-ui: 'Montserrat', 'Avenir Next', 'Segoe UI', system-ui, sans-serif;

  color-scheme: light;
  margin: 0; background: var(--z-paper); color: var(--z-ink);
  font-family: var(--z-font-ui); line-height: 1.6; font-size: 1rem;
}
.z-issue, .z-issue *, .z-issue *::before, .z-issue *::after { box-sizing: border-box; }
html:has(.z-issue) { scroll-behavior: smooth; }
@media (prefers-reduced-motion: reduce) {
  html:has(.z-issue) { scroll-behavior: auto; }
  .z-issue *, .z-issue *::before, .z-issue *::after { animation: none !important; transition: none !important; }
}
.z-issue button { font-family: inherit; cursor: pointer; }
.z-issue button:focus-visible, .z-issue a:focus-visible, .z-issue input:focus-visible { outline: 2px solid var(--z-red); outline-offset: 2px; }

.z-page { max-width: 880px; margin: 0 auto; padding: 0 1.4rem; }

/* ═══ COVER ═══ */
.z-cover {
  position: relative; overflow: hidden;
  background: var(--z-red);
  color: var(--z-paper);
  /* halftone press texture */
  background-image:
    radial-gradient(rgba(26,16,24,.16) 1px, transparent 1.5px),
    linear-gradient(180deg, #D41920, #B5131A);
  background-size: 9px 9px, 100% 100%;
}
.z-cover .z-page { padding-top: 2rem; padding-bottom: 3.4rem; }
.z-masthead {
  display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: .5rem;
  border-bottom: 2px solid rgba(255,248,245,.4); padding-bottom: .9rem; margin-bottom: 2.6rem;
  font-family: var(--z-font-cond); text-transform: uppercase; letter-spacing: .22em; font-size: .78rem; font-weight: 600;
}
.z-masthead .z-brand b { letter-spacing: .28em; }
.z-cover-issue {
  font-family: var(--z-font-cond); font-size: .85rem; letter-spacing: .3em; text-transform: uppercase;
  color: var(--z-baby); margin: 0 0 .8rem;
}
.z-cover h1 {
  font-family: var(--z-font-cond); text-transform: uppercase; font-weight: 700;
  font-size: clamp(3rem, 9.5vw, 5.6rem); line-height: .92; letter-spacing: .005em;
  margin: 0 0 1.4rem; max-width: 12ch; text-wrap: balance;
}
.z-cover h1 .z-outline-word {
  color: transparent; -webkit-text-stroke: 2px var(--z-paper);
}
.z-cover .z-dek {
  font-family: var(--z-font-serif); font-style: italic; font-size: 1.2rem; line-height: 1.5;
  max-width: 44ch; color: #FCE3E0; margin: 0 0 2rem;
}
.z-cover-meta { display: flex; gap: .7rem; flex-wrap: wrap; }
.z-stamp {
  font-family: var(--z-font-cond); font-size: .72rem; font-weight: 600; letter-spacing: .2em; text-transform: uppercase;
  border: 1.5px solid rgba(255,248,245,.65); border-radius: 999px; padding: .38rem .9rem;
}
.z-stamp.z-stamp--solid { background: var(--z-ink); border-color: var(--z-ink); color: var(--z-paper); }

/* cover body: text, and (with a class image) the photo print on the right */
.z-cover-body--print { display: grid; grid-template-columns: minmax(0, 1fr) 400px; gap: 2.4rem; align-items: center; }
.z-cover-print { margin: 0; justify-self: end; width: 100%; max-width: 400px; position: relative; transform: rotate(-2.5deg); }
.z-cover-print-paper {
  position: relative; background: var(--z-paper); padding: 10px 10px 40px;
  box-shadow: 0 18px 40px rgba(26,16,24,.35), 0 2px 6px rgba(26,16,24,.25);
}
.z-cover-print-paper img { display: block; width: 100%; aspect-ratio: 3 / 2; object-fit: cover; filter: saturate(.92) contrast(1.04); }
.z-cover-print-paper figcaption {
  position: absolute; left: 14px; right: 14px; bottom: 11px;
  display: flex; justify-content: space-between; align-items: baseline; gap: .6rem; color: var(--z-ink);
}
.z-cover-print-paper figcaption em { font-family: var(--z-font-serif); font-style: italic; font-size: .95rem; color: var(--z-mid); }
.z-cover-print-paper figcaption span { font-family: var(--z-font-cond); font-size: .66rem; letter-spacing: .22em; text-transform: uppercase; color: var(--z-red); font-weight: 600; white-space: nowrap; }
/* tape strips on the print */
.z-cover-print::before, .z-cover-print::after {
  content: ''; position: absolute; width: 92px; height: 24px; background: rgba(248,215,221,.72);
  box-shadow: 0 1px 2px rgba(0,0,0,.08); z-index: 2;
}
.z-cover-print::before { top: -12px; left: 34px; transform: rotate(-9deg); }
.z-cover-print::after { bottom: -10px; right: 28px; transform: rotate(12deg); }
@media (max-width: 720px) {
  .z-cover-body--print { grid-template-columns: 1fr; gap: 1.6rem; }
  .z-cover-print { order: -1; justify-self: center; max-width: 320px; transform: rotate(-2deg); margin-bottom: .4rem; }
  .z-cover-print-paper { padding: 8px 8px 30px; }
  .z-cover-print-paper figcaption { left: 10px; right: 10px; bottom: 8px; }
  .z-cover-print-paper figcaption em { font-size: .8rem; }
  .z-cover-print::before, .z-cover-print::after { width: 74px; height: 20px; }
}

/* tape corner deco */
.z-tape {
  position: absolute; width: 130px; height: 34px; background: rgba(248,215,221,.5);
  top: 26px; right: -34px; transform: rotate(38deg);
}

/* ═══ AGENDA (In this class) ═══ */
.z-agenda-band { background: var(--z-ink); color: var(--z-paper); }
.z-agenda-band .z-page { padding-top: 2.6rem; padding-bottom: 2.8rem; }
.z-band-label {
  font-family: var(--z-font-cond); font-size: .78rem; font-weight: 600; letter-spacing: .3em;
  text-transform: uppercase; color: var(--z-charm); margin: 0 0 1.4rem;
  display: flex; align-items: center; gap: 1rem;
}
.z-band-label::after { content: ''; flex: 1; height: 1px; background: rgba(242,160,196,.35); }
.z-contents-list { list-style: none; margin: 0; padding: 0; }
.z-contents-row {
  display: grid; grid-template-columns: 3.2rem 1fr auto auto; gap: 1.1rem; align-items: baseline;
  padding: .85rem 0; text-decoration: none; color: inherit;
  border: 0; border-bottom: 1px dashed rgba(255,248,245,.22);
  width: 100%; background: none; font: inherit; text-align: left; cursor: pointer;
}
.z-contents-row:hover .z-t { color: var(--z-charm); }
.z-contents-list .z-no {
  font-family: var(--z-font-cond); font-weight: 700; font-size: 1.5rem; color: var(--z-sizzle);
  font-variant-numeric: tabular-nums;
}
.z-contents-list .z-t { font-family: var(--z-font-cond); text-transform: uppercase; letter-spacing: .06em; font-weight: 600; font-size: 1.02rem; transition: color .15s; }
.z-contents-list small { display: block; font-family: var(--z-font-ui); text-transform: none; letter-spacing: 0; font-weight: 400; font-size: .76rem; color: #C9AEB4; margin-top: .1rem; }
.z-contents-list .z-kind {
  font-family: var(--z-font-cond); font-size: .66rem; letter-spacing: .2em; text-transform: uppercase;
  border: 1px solid rgba(255,248,245,.4); border-radius: 999px; padding: .22rem .6rem; color: var(--z-baby);
}
.z-contents-list .z-mins { font-size: .74rem; color: #C9AEB4; font-variant-numeric: tabular-nums; }
.z-contents-list .z-done-dot { color: var(--z-gold); }

/* ═══ SPREADS ═══ */
.z-spread { scroll-margin-top: 1.5rem; }
.z-spread .z-page { padding-top: 3.4rem; padding-bottom: 3rem; }
.z-spread + .z-spread .z-page { border-top: 3px solid var(--z-ink); }

.z-spread-head { display: grid; grid-template-columns: auto 1fr; gap: 1.4rem; align-items: start; margin-bottom: 1.6rem; }
.z-bigno {
  font-family: var(--z-font-cond); font-weight: 700; font-size: 4.6rem; line-height: .8;
  color: transparent; -webkit-text-stroke: 2px var(--z-red);
}
.z-spread-eyebrow {
  font-family: var(--z-font-cond); font-size: .72rem; font-weight: 600; letter-spacing: .28em;
  text-transform: uppercase; color: var(--z-sizzle); margin: .3rem 0 .4rem;
}
.z-homework-tag {
  display: inline-block; margin-right: .6em; padding: .1em .55em; border-radius: 999px;
  background: var(--z-red); color: #fff; letter-spacing: .18em; font-size: .68rem; vertical-align: middle;
}
.z-spread-head h2 {
  font-family: var(--z-font-cond); text-transform: uppercase; font-weight: 700;
  font-size: clamp(1.9rem, 5vw, 2.7rem); line-height: .98; margin: 0; letter-spacing: .01em; text-wrap: balance;
}

.z-cols { display: grid; grid-template-columns: 1.5fr 1fr; gap: 2.2rem; align-items: start; }
@media (max-width: 720px) { .z-cols { grid-template-columns: 1fr; } }
.z-prose p { margin: 0 0 1rem; max-width: 62ch; }
.z-prose .z-lead { font-family: var(--z-font-serif); font-style: italic; font-size: 1.15rem; color: var(--z-mid); }
.z-issue mark { background: linear-gradient(transparent 60%, var(--z-baby) 60%); color: inherit; padding: 0 .08em; font-weight: 600; }

/* clipped vocab cards */
.z-clip {
  background: #fff; border: 1px solid var(--z-line); border-radius: 4px;
  padding: 1rem 1.1rem; margin-bottom: 1rem; position: relative;
  transform: rotate(.6deg); box-shadow: 0 6px 16px -10px rgba(26,16,24,.25);
}
.z-clip.z-clip--alt { transform: rotate(-.8deg); }
.z-clip::before {
  content: ''; position: absolute; top: -9px; left: 50%; transform: translateX(-50%) rotate(-2deg);
  width: 58px; height: 16px; background: rgba(242,160,196,.55);
}
.z-clip b { font-family: var(--z-font-cond); text-transform: uppercase; font-size: 1rem; letter-spacing: .04em; }
.z-clip i { display: block; font-size: .76rem; color: var(--z-sizzle); font-style: normal; font-weight: 600; letter-spacing: .12em; text-transform: uppercase; margin: .1rem 0 .3rem; }
.z-clip p { margin: 0; font-size: .84rem; color: var(--z-mid); }

/* audio strip */
.z-audio-strip {
  display: flex; align-items: center; gap: 1rem;
  background: #fff; border: 1px solid var(--z-line); border-left: 6px solid var(--z-red);
  border-radius: 4px; padding: .9rem 1.1rem; margin: 1.4rem 0 .4rem;
}
.z-play {
  width: 44px; height: 44px; border-radius: 50%; border: 0; flex: none;
  background: var(--z-red); color: #fff; display: grid; place-items: center;
}
.z-play svg.z-tri { margin-left: 3px; }
.z-ticks { flex: 1; display: flex; gap: 3px; align-items: center; height: 30px; touch-action: none; cursor: pointer; }
.z-ticks i { width: 3px; border-radius: 2px; background: var(--z-baby); }
.z-ticks i.z-past { background: var(--z-sizzle); }
.z-audio-strip .z-time { font-size: .76rem; color: var(--z-warm-gray); font-variant-numeric: tabular-nums; }
.z-caption { font-size: .74rem; color: var(--z-warm-gray); margin: .4rem 0 0; }

/* ═══ WORKBOOK PANELS ═══ */
.z-workbook {
  border: 2px dashed var(--z-red-applied); border-radius: 6px; background: var(--z-cream);
  padding: 1.5rem 1.6rem; margin-top: 1.8rem; position: relative;
}
.z-wb-tab {
  position: absolute; top: -14px; left: 18px;
  background: var(--z-red-applied); color: #fff;
  font-family: var(--z-font-cond); font-size: .7rem; font-weight: 700; letter-spacing: .24em; text-transform: uppercase;
  padding: .3rem .85rem; border-radius: 3px; transform: rotate(-1.2deg);
}
.z-workbook .z-wb-title { font-family: var(--z-font-cond); text-transform: uppercase; font-size: 1.25rem; margin: .3rem 0 .3rem; letter-spacing: .02em; }
.z-workbook .z-hint { font-size: .82rem; color: var(--z-warm-gray); margin: 0 0 1rem; }

.z-opts { display: grid; gap: .55rem; margin-bottom: 1rem; }
.z-opt {
  text-align: left; background: #fff; border: 1.5px solid var(--z-line);
  border-radius: 4px; padding: .75rem .95rem; font-size: .92rem; color: var(--z-ink);
  display: flex; gap: .75rem; align-items: baseline; transition: border-color .12s, background .12s;
}
.z-opt:hover { border-color: var(--z-charm); }
.z-opt .z-key { font-family: var(--z-font-cond); font-weight: 700; font-size: .78rem; color: var(--z-warm-gray); border: 1px solid var(--z-line); border-radius: 3px; padding: .1rem .5rem; flex: none; }
.z-opt.z-opt--picked { border-color: var(--z-red-applied); background: var(--z-baby); }
.z-opt.z-opt--picked .z-key { border-color: var(--z-red-applied); color: var(--z-red-applied); background: #fff; }
.z-opt.z-opt--right { border-color: var(--z-good); background: var(--z-good-soft); }
.z-opt.z-opt--right .z-key { border-color: var(--z-good); color: var(--z-good); background: #fff; }

.z-blank {
  font-family: var(--z-font-ui); font-size: .95rem; font-weight: 600; color: var(--z-red-applied);
  border: 0; border-bottom: 2px solid var(--z-charm); background: transparent;
  width: 8ch; text-align: center; padding: .1rem .2rem;
}
.z-blank::placeholder { color: #D3A9B4; font-weight: 400; }
.z-blank:focus { border-bottom-color: var(--z-red-applied); }
.z-blank.z-blank--right { color: var(--z-good); border-bottom-color: var(--z-good); }
.z-blank.z-blank--wrong { color: var(--z-red-applied); border-bottom-color: var(--z-red-applied); }

.z-wb-foot { display: flex; align-items: center; gap: .9rem; flex-wrap: wrap; }
.z-btn {
  border: 0; border-radius: 999px; padding: .68rem 1.5rem; font-weight: 700; font-size: .85rem;
  background: linear-gradient(90deg, #CF2E2E, #F05F7A 55%, #F78DA7); color: #fff;
  box-shadow: 0 8px 18px -9px rgba(207,46,46,.55);
  transition: transform .13s;
}
.z-btn:hover { transform: translateY(-2px); }
.z-btn.z-btn--dark { background: var(--z-ink); box-shadow: none; }
/* not ready yet (nothing to submit): visibly off */
.z-btn:disabled { opacity: .5; cursor: not-allowed; box-shadow: none; }
.z-btn:disabled:hover { transform: none; }
/* working (answer being processed): stays readable, shows the spinner */
.z-btn.z-btn--loading { display: inline-flex; align-items: center; justify-content: center; gap: .5rem; opacity: .85; cursor: progress; }
.z-spinner {
  flex: none; width: .9em; height: .9em; border-radius: 50%;
  border: 2px solid currentColor; border-top-color: transparent;
  animation: z-spin .8s linear infinite;
}
@keyframes z-spin { to { transform: rotate(360deg); } }
.z-attempt { font-size: .72rem; color: var(--z-warm-gray); }
.z-verdict { display: none; margin-top: .9rem; background: var(--z-good-soft); border: 1px solid #BFDECB; color: var(--z-good); font-size: .84rem; font-weight: 600; border-radius: 4px; padding: .7rem .95rem; }
.z-verdict.z-show { display: block; }

/* speaking panel */
.z-speak-panel {
  display: grid; grid-template-columns: 64px 1fr auto; gap: 1.1rem; align-items: center;
  background: var(--z-ink); color: var(--z-paper); border-radius: 6px; padding: 1.3rem 1.4rem; margin-top: 1.8rem;
}
@media (max-width: 640px) { .z-speak-panel { grid-template-columns: 64px 1fr; } .z-speak-panel .z-speak-action { grid-column: 1 / -1; } }
.z-speak-panel .z-avatar {
  width: 64px; height: 64px; border-radius: 50%;
  background: radial-gradient(circle at 32% 28%, #F78DA7, var(--z-red) 75%);
  display: grid; place-items: center; font-family: var(--z-font-cond); font-weight: 700; font-size: 1.5rem; color: #fff;
  box-shadow: 0 0 0 4px rgba(242,160,196,.35);
}
.z-speak-panel .z-speak-title { margin: 0 0 .2rem; font-family: var(--z-font-cond); text-transform: uppercase; letter-spacing: .06em; font-size: 1.05rem; }
.z-speak-panel .z-speak-desc { margin: 0; font-size: .8rem; color: #D9C7CC; max-width: 44ch; }
/* exercise instructions inside the panel: light text on ink, under the header row */
.z-speak-panel .z-speak-intro {
  grid-column: 1 / -1; margin-top: .2rem; padding-top: 1rem;
  border-top: 1px solid rgba(255,255,255,.14); font-size: .92rem; line-height: 1.55; max-width: 64ch;
}
.z-speak-panel .z-speak-intro p, .z-speak-panel .z-speak-intro li { color: #EFE4E6; }
.z-speak-panel .z-speak-intro h1, .z-speak-panel .z-speak-intro h2, .z-speak-panel .z-speak-intro h3 { color: var(--z-paper); font-size: 1rem; }
.z-speak-panel .z-speak-intro strong { color: #fff; }
.z-speak-panel .z-speak-intro a { color: #F2A0C4; text-decoration: underline; }
.z-speak-panel .z-speak-intro blockquote { border-left-color: rgba(255,255,255,.3); background: rgba(255,255,255,.06); color: #EFE4E6; }
.z-speak-panel .z-speak-extra { grid-column: 1 / -1; }

/* ═══ INTERLUDE QUOTE ═══ */
.z-interlude { background: var(--z-baby); }
.z-interlude .z-page { padding-top: 3rem; padding-bottom: 3rem; text-align: center; }
.z-interlude .z-q {
  font-family: var(--z-font-serif); font-style: italic;
  font-size: clamp(1.4rem, 3.6vw, 2rem); line-height: 1.35; color: var(--z-ink);
  max-width: 26ch; margin: 0 auto .9rem; text-wrap: balance;
}
.z-interlude .z-who { font-family: var(--z-font-cond); font-size: .74rem; letter-spacing: .3em; text-transform: uppercase; color: var(--z-red); font-weight: 600; margin: 0; }

/* ═══ BACK COVER / WALL ═══ */
.z-backcover { background: var(--z-ink); color: var(--z-paper); }
.z-backcover .z-page { padding-top: 3rem; padding-bottom: 3.6rem; text-align: center; }
.z-backcover h2 { font-family: var(--z-font-cond); text-transform: uppercase; font-size: clamp(1.8rem, 5vw, 2.5rem); margin: 0 0 .5rem; line-height: 1; }
.z-backcover .z-sub { color: #C9AEB4; font-size: .88rem; margin: 0 0 2rem; }
.z-wall { display: flex; justify-content: center; gap: .9rem; flex-wrap: wrap; margin-bottom: 2.2rem; }
.z-mini-poster {
  width: 74px; height: 98px; border-radius: 3px; position: relative;
  font-family: var(--z-font-cond); font-weight: 700; font-size: .58rem; text-transform: uppercase;
  display: grid; place-items: center; padding: .4rem; text-align: center; line-height: 1.15;
  box-shadow: 0 8px 18px -8px rgba(0,0,0,.55);
}
.z-mini-poster::before { content: ''; position: absolute; top: -6px; left: 50%; transform: translateX(-50%) rotate(-2deg); width: 34px; height: 10px; background: rgba(248,215,221,.6); }
.z-mp1 { background: var(--z-red); color: var(--z-paper); transform: rotate(-2.2deg); }
.z-mp2 { background: var(--z-paper); color: var(--z-red); transform: rotate(1.4deg); }
.z-mp3 { background: var(--z-charm); color: var(--z-ink); transform: rotate(-.8deg); }
.z-mp4 { background: #2A1B23; color: var(--z-charm); transform: rotate(2deg); border: 1px solid rgba(242,160,196,.4); }
.z-mini-poster.z-next {
  background: transparent; border: 2px dashed rgba(255,248,245,.45); color: rgba(255,248,245,.7);
  transform: rotate(1deg); box-shadow: none;
}
.z-mini-poster.z-next::before { display: none; }
.z-backcover .z-cta {
  font-family: var(--z-font-cond); font-size: 1rem; font-weight: 700; letter-spacing: .18em; text-transform: uppercase;
  border: 0; border-radius: 999px; padding: 1rem 2.4rem;
  background: linear-gradient(90deg, #CF2E2E, #F05F7A 55%, #F78DA7); color: #fff;
  box-shadow: 0 6px 0 #8E1218, 0 18px 30px -12px rgba(212,25,32,.55);
  transition: transform .13s, box-shadow .13s;
}
.z-backcover .z-cta:hover:not(:disabled) { transform: translateY(-2px); }
.z-backcover .z-cta:active:not(:disabled) { transform: translateY(4px); box-shadow: 0 2px 0 #8E1218; }
/* Completing a class is a multi-round-trip mutation that can take seconds.
   Without a pressed/dimmed state the button looks untouched and the click
   reads as "nothing happened". */
.z-backcover .z-cta:disabled {
  cursor: progress; opacity: .6; filter: saturate(.65);
  transform: translateY(4px); box-shadow: 0 2px 0 #8E1218;
}
.z-backcover .z-next-teaser { margin-top: 1.6rem; font-size: .76rem; color: #C9AEB4; }
.z-backcover .z-next-teaser b { color: var(--z-paper); }
/* ═══ FOLIO PILL (block navigation) ═══ */
/* Centred under the reading column (--z-folio-x is measured by the component,
   so an open app sidebar doesn't skew it) like a printed folio. The AI chat
   launcher owns the bottom-right corner, so the pill never goes there; on
   phones it anchors bottom-left and leaves the launcher's 7rem clear. */
.z-folio {
  position: fixed; left: var(--z-folio-x, 50%); bottom: calc(1rem + env(safe-area-inset-bottom, 0px)); z-index: 40;
  transform: translateX(-50%);
  display: flex; align-items: center; max-width: calc(100vw - 2rem);
  background: var(--z-ink); color: var(--z-paper); border-radius: 999px; padding: .25rem;
  box-shadow: 0 12px 28px -12px rgba(26,16,24,.7), 0 0 0 1px rgba(255,248,245,.08) inset;
  font-family: var(--z-font-cond);
  transition: transform .25s ease, opacity .25s ease;
}
.z-folio--hidden { transform: translate(-50%, calc(100% + 1.5rem)); opacity: 0; pointer-events: none; }
@media (max-width: 720px) {
  .z-folio { left: 1rem; transform: none; max-width: calc(100vw - 2rem - 7rem); }
  .z-folio--hidden { transform: translateY(calc(100% + 1.5rem)); }
}
.z-folio button { background: none; border: 0; color: inherit; font-family: inherit; padding: 0; display: flex; align-items: center; justify-content: center; }
.z-folio-arrow { width: 44px; height: 44px; border-radius: 50%; flex: none; }
.z-folio-arrow:hover:not(:disabled) { background: rgba(255,248,245,.12); }
.z-folio-arrow:disabled { opacity: .3; cursor: default; }
.z-folio-mid {
  flex-direction: column; align-items: flex-start; min-width: 0; max-width: 15rem;
  padding: .2rem .55rem; line-height: 1.15; border-radius: 999px;
}
.z-folio-mid:hover { background: rgba(255,248,245,.08); }
.z-folio-mid em { font-style: normal; font-size: .64rem; letter-spacing: .2em; text-transform: uppercase; color: var(--z-charm); font-variant-numeric: tabular-nums; }
.z-folio-mid b { font-size: .82rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.z-folio button:focus-visible { outline: 2px solid var(--z-charm); outline-offset: 2px; }
.z-folio-prog { position: absolute; left: 1.1rem; right: 1.1rem; bottom: 0; height: 2px; border-radius: 2px; background: rgba(255,248,245,.14); overflow: hidden; }
.z-folio-prog i { display: block; height: 100%; background: var(--z-charm); transition: width .25s ease; }

/* the sheet: the class agenda lifted from the bottom */
.z-folio-sheet { position: fixed; inset: 0; z-index: 60; display: flex; align-items: flex-end; justify-content: center; }
.z-folio-dim { position: absolute; inset: 0; background: rgba(26,16,24,.5); backdrop-filter: blur(2px); }
.z-folio-panel {
  position: relative; width: 100%; max-width: 560px; max-height: min(78vh, 78dvh); overflow-y: auto;
  background: var(--z-ink); color: var(--z-paper); border-radius: 18px 18px 0 0;
  padding: .6rem 1.4rem calc(1.2rem + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -20px 50px -20px rgba(26,16,24,.8);
  animation: z-folio-up .24s ease;
}
@keyframes z-folio-up { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
.z-folio-grab { width: 40px; height: 4px; border-radius: 2px; background: rgba(255,248,245,.35); margin: 0 auto .9rem; }
.z-folio-head { display: flex; align-items: center; gap: 1rem; margin-bottom: .4rem; }
.z-folio-head .z-band-label { flex: 1; margin: 0; }
.z-folio-close {
  width: 40px; height: 40px; border-radius: 50%; flex: none;
  background: none; border: 1px solid rgba(255,248,245,.35); color: var(--z-paper);
  display: flex; align-items: center; justify-content: center; padding: 0;
}
.z-folio-close:hover { background: rgba(255,248,245,.12); border-color: var(--z-paper); }
.z-folio-list { list-style: none; margin: 0; padding: 0; }
.z-folio-row {
  display: grid; grid-template-columns: 2.6rem 1fr auto; gap: .9rem; align-items: center;
  min-height: 3.4rem; padding: .7rem .6rem; margin: 0 -.6rem; width: calc(100% + 1.2rem);
  background: none; border: 0; border-radius: 6px; border-bottom: 1px dashed rgba(255,248,245,.18);
  color: inherit; text-align: left; cursor: pointer; font-family: var(--z-font-cond);
  transition: background .12s;
}
.z-folio-row:hover { background: rgba(255,248,245,.06); }
.z-folio-row:hover .z-folio-title { color: var(--z-charm); }
.z-folio-no { font-weight: 700; font-size: 1.4rem; line-height: 1; color: var(--z-sizzle); font-variant-numeric: tabular-nums; }
.z-folio-text { min-width: 0; display: flex; flex-direction: column; gap: .15rem; }
.z-folio-title { font-size: .98rem; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; line-height: 1.15; transition: color .15s; }
.z-folio-text small { font-family: var(--z-font-ui); font-size: .72rem; letter-spacing: 0; color: #C9AEB4; }
.z-folio-cell { display: flex; justify-content: flex-end; min-width: 3rem; }
.z-folio-done { font-family: var(--z-font-ui); font-size: .74rem; color: var(--z-gold); white-space: nowrap; }
.z-folio-now {
  font-size: .62rem; font-weight: 700; letter-spacing: .22em; text-transform: uppercase;
  color: var(--z-ink); background: var(--z-charm); border-radius: 999px; padding: .22rem .6rem;
}
.z-folio-row--now { background: rgba(242,160,196,.1); }
.z-folio-row--now .z-folio-no, .z-folio-row--now .z-folio-title { color: var(--z-charm); }
.z-folio-row:focus-visible { outline: 2px solid var(--z-charm); outline-offset: -2px; }
@media (max-width: 720px) {
  .z-folio-mid { max-width: 10.5rem; }
}
@media (prefers-reduced-motion: reduce) {
  .z-folio, .z-folio-prog i, .z-folio-panel { transition: none; animation: none; }
}
`;


/**
 * Renders the zine stylesheet once. Include a single instance on any page
 * that uses zine components (ZineIssue already does this for you).
 */
export function ZineStyles() {
  return <style dangerouslySetInnerHTML={{ __html: zineCss }} />;
}

export interface ZineIssueProps {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Root wrapper for a zine "issue" page: injects the stylesheet and provides
 * the `.z-issue` scope (paper background, ink text, UI font, light theme).
 */
export function ZineIssue({ children, className, style }: ZineIssueProps) {
  return (
    <div className={cn(ZINE_ROOT_CLASS, className)} style={style}>
      <ZineStyles />
      {children}
    </div>
  );
}
